import { isPlatformBrowser, AsyncPipe, Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  Inject,
  OnInit,
  PLATFORM_ID,
  signal,
  ɵglobal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, concatMap, delay, filter, merge, mergeMap, Observable, of, take, withLatestFrom } from 'rxjs';
import { EnvironmentService } from '../../environments/environment.service';
import { AuthLoginState, LoginType, Token } from '../core/auth/auth.models';
import { selectAuthLoginState } from '../core/auth/auth.selectors';
import { LogService } from '../core/log/log.service';
import { UserDTO } from '../domain/user/user.model';
import { selectUser } from '../domain/user/user.selectors';
import { selectCurrentTopic } from '../domain/topic/topic.selector';
import { Topic } from '../domain/topic/topic.model';
import { ParamMap, RouterOutlet } from '@angular/router';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { FooterComponent } from '../shared/footer/footer.component';
import { ToolbarComponent } from '../shared/toolbar/toolbar.component';
import { SidenavComponent } from '../shared/sidenav/sidenav.component';
import { ConsentManagementComponent } from '../shared/consent-management/consent-management.component';
import { LocalStorageService } from '../core/local-storage/local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { evaluateBooleanFromString, getQueryParametersFromURL } from '../shared/util/util';
import { activateUser, loadUserGuest } from '../domain/user/user.actions';
import { authGuestLogin, authSoftLogin, authUserLoadedGuest, setLocalStorageAuth } from '../core/auth/auth.actions';
import { onlyWhenAuthenticated, onlyWhenOnline } from '../shared/util/operators';
import { createSession } from '../core/session/session.actions';
import { ExternalConsent } from '../domain/external-consent/external-consent.model';
import { ExternalConsentActions } from '../domain/external-consent/external-consent.actions';
import { dnLivreaConfig } from '../core/init/init.actions';
import { loadTopic } from '../domain/topic/topic.actions';
import { loadTemplateCt } from '../domain/template-ct/template-ct.actions';
import { GA4ConfigService } from '../shared/gtag/ga4-dual-tracking';

@Component({
  selector: 'dottnet-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    AsyncPipe,
    FooterComponent,
    ToolbarComponent,
    SidenavComponent,
    ConsentManagementComponent,
    MatSidenav,
    MatSidenavContent,
    MatSidenavContainer,
  ],
})
export class AppComponent implements OnInit {
  isProd: boolean;
  envName: string;
  fbLink: string;
  twitterLink: string;
  linkedinLink: string;
  instagramLink: string;
  version: any;
  g4PropertyId: string;

  // Signal per lo stato di autenticazione
  authLoginState = signal<AuthLoginState | null>(null);
  // Observable originale (mantenuto per compatibilità con altri componenti)
  authLoginState$: Observable<AuthLoginState>;

  user$: Observable<UserDTO>;
  searchText: string;
  topic$: Observable<Topic>;

  isBrowser: boolean = false;
  //private ngZone = inject(NgZone);
  constructor(
    private store: Store,
    private environmentService: EnvironmentService,
    private logService: LogService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private localStorageService: LocalStorageService,
    private envService: EnvironmentService,
    private translateService: TranslateService,
    private location: Location,
    private ga4Service: GA4ConfigService,
    @Inject(DOCUMENT) private document: Document
    //private appRef: ApplicationRef // Inject ApplicationRef
  ) {
    /*
    const ngZone = ɵglobal.Zone;
    const TaskTrackingZone = ngZone.current._parent?._properties?.TaskTrackingZone;

    if (!TaskTrackingZone) {
      return;
    }
      */
  }
  ngOnInit(): void {
    this.g4PropertyId = this.environmentService.ga4Property;
    this.ga4Service.configGlobalProperty(this.g4PropertyId); // Configuro la property globale
    this.isBrowser = isPlatformBrowser(this.platformId);


    // Debug ApplicationRef.isStable to track hydration stability
    // this.appRef.isStable.subscribe(stable => this.logService.info('ApplicationRef.isStable: ' + stable));

    /*
    // print all zone tasks to understand which tasks are running and are making the app unstable
    if (ɵglobal.Zone) {
      ɵglobal.Zone.current.fork({
        name: 'debug',
        onHasTask: (delegate, current, target, hasTask) => {
          console.log('Zone hasTask:', hasTask);
        }
      });

    }
    */

    this.isProd = this.environmentService.production;
    this.envName = this.environmentService.envName;
    this.version = this.environmentService.versions.app;
    this.fbLink = this.environmentService.fbLink;
    this.twitterLink = this.environmentService.twitterLink;
    this.linkedinLink = this.environmentService.linkedinLink;
    this.instagramLink = this.environmentService.instagramLink;

    this.logService.init(true, true, false, this.envService.logLevel);
    this.logService.info('logService started');


    this.store.dispatch(loadTemplateCt());
    this.store.dispatch(loadTopic());
    this.store.dispatch(dnLivreaConfig());

    if (isPlatformBrowser(this.platformId)) {
      this.logService.info('Startup - Dottnet Running in browser environment');
    } else {
      this.logService.info('Startup - Dottnet Running in SSR environment');
    }

    if (isPlatformBrowser(this.platformId)) {

      this.translateService.use(this.envService.defaultLanguage);  //Setto il file di lingua da utilizzare
      this.document.documentElement.lang = this.envService.defaultLanguage;

      // this gets only the path, without domain. In this case it's just what we need
      const urlPath: string = this.location.path(true); // includes fragment
      const params: ParamMap = getQueryParametersFromURL(urlPath);

      let oldSessionID = <number>this.localStorageService.getItem('SESSIONID');
      if (oldSessionID === null) {
        oldSessionID = 0;
      }

      const renewToken = <Token>this.localStorageService.getItem('RENEW');
      const currentLoginType = <LoginType>this.localStorageService.getItem('LOGINTYPE');

      let softToken: string = '';
      let confirmationMailToken: string = '';
      let renewFound: boolean = false;
      let softFound: boolean = false;

      if (params.has('tkcm') && params.get('tkcm').length > 0) {
        let referrer: string = '';
        if (isPlatformBrowser(this.platformId)) {
          referrer = this.document.referrer;
        }

        confirmationMailToken = params.get('tkcm');
        this.store.dispatch(
          activateUser({
            token: confirmationMailToken,
            callbackUrl: urlPath,
            params: params,
            oldSessionID: oldSessionID,
            url: urlPath,
            referrer: referrer,
          })
        );
      } else {
        if (params.has('tksl') && params.get('tksl').length > 0) {
          softToken = params.get('tksl');
          softFound = true;
        }
      }

      if (renewToken !== null && currentLoginType !== null) {
        this.logService.info('Startup -Found renew token in local storage: ', renewToken.token);
        this.logService.info('Startup -Found login type in local storage: ', currentLoginType);

        const scadenzaRenewToken = renewToken.expires;
        const adesso = Math.floor(Date.now() / 1000);
        if (scadenzaRenewToken > adesso && scadenzaRenewToken - adesso > 600) {
          renewFound = true;
        } else {
          this.logService.info(
            'Startup -Removing renew token because expired: ',
            renewToken.token
          );
          this.localStorageService.removeItem('RENEW');
          this.localStorageService.removeItem('LOGINTYPE');
        }
      }

      if (renewFound && softFound && currentLoginType === LoginType.HARDLOGIN) {
        this.store.dispatch(setLocalStorageAuth({ token: renewToken, loginType: currentLoginType }));
      }

      if (
        renewFound &&
        softFound &&
        (currentLoginType === LoginType.SOFTLOGIN || currentLoginType === LoginType.GUESTLOGIN)
      ) {
        this.store.dispatch(authSoftLogin({ token: softToken }));
      }

      if (renewFound && !softFound) {
        this.store.dispatch(setLocalStorageAuth({ token: renewToken, loginType: currentLoginType }));
      }

      if (!renewFound && softFound) {
        this.store.dispatch(authSoftLogin({ token: softToken }));
      }

      if (!renewFound && !softFound) {
        this.logService.info(
          'Startup -Renew token not found in local storage. Doing guest login: ',
          undefined
        );
        this.store.dispatch(authGuestLogin());
      }

      this.store.select(selectAuthLoginState).pipe(
        onlyWhenOnline(this.store),
        onlyWhenAuthenticated(this.store),
        take(1),
        withLatestFrom(this.store.select(selectUser)),
        mergeMap(([authLoginState, userDTO]) => {
          // just path, without domain
          const url: string = this.location.path(true); // includes fragment
          const params: ParamMap = getQueryParametersFromURL(url);

          let oldSessionID = <number>this.localStorageService.getItem('SESSIONID');
          if (oldSessionID === null) {
            oldSessionID = 0;
          }

          this.logService.info(
            'Startup -Creating session. current login state: ',
            authLoginState.toString()
          );

          let referrer: string = '';
          if (isPlatformBrowser(this.platformId)) {
            referrer = this.document.referrer;
          }

          this.store.dispatch(
            createSession({
              params: params,
              oldSessionID: oldSessionID,
              url: url,
              referrer: referrer,
            })
          );

          // If the user is authenticated, anf if we have the right parameters, we can save privacy consent automatically
          // just 1 flag is mandatory, the others are optional
          this.logService.info(
            'Startup - User authenticated, checking for external consent parameters'
          );
          if (
            params.has('tag') &&
            params.get('tag').length > 0 &&
            params.has('num') &&
            params.get('num').length > 0 &&
            params.has('flg1') &&
            params.get('flg1').length > 0
          ) {
            let externalConsentPayload: ExternalConsent = <ExternalConsent>{};

            this.logService.info('Startup - External consent parameters found, creating payload');

            externalConsentPayload.tagGlobale = parseInt(params.get('tag'), 10);
            externalConsentPayload.numberId = parseInt(params.get('num'), 10);
            externalConsentPayload.idAnagrafica = userDTO.idAnagrafica;
            externalConsentPayload.flag1 = evaluateBooleanFromString(params.get('flg1'));

            if (params.has('flg2') && params.get('flg2').length > 0) {
              externalConsentPayload.flag2 = evaluateBooleanFromString(params.get('flg2'));
            }

            if (params.has('flg3') && params.get('flg3').length > 0) {
              externalConsentPayload.flag3 = evaluateBooleanFromString(params.get('flg3'));
            }

            if (params.has('flg4') && params.get('flg4').length > 0) {
              externalConsentPayload.flag4 = evaluateBooleanFromString(params.get('flg4'));
            }

            this.store.dispatch(ExternalConsentActions.try({ externalConsent: externalConsentPayload }));
          }
          return of(null);
        })).subscribe();

    } else {
      this.translateService.use(this.environmentService.defaultLanguage);
      this.logService.info('Startup - Dispatch GuestLogin auth for SSR');
      this.store.dispatch(authUserLoadedGuest());
      this.store.dispatch(loadUserGuest({}));
    }

    // Inizializzazione dell'Observable authLoginState$
    this.authLoginState$ = this.store.select(selectAuthLoginState);

    // Sottoscrizione all'Observable per aggiornare il signal
    this.authLoginState$.subscribe((state) => {
      if (state) {
        this.authLoginState.set(state);
      }
    });

    this.user$ = this.store.select(selectUser);
    this.topic$ = this.store.select(selectCurrentTopic);

    if (!this.isBrowser) {
      this.logService.info('Start rendering app server-side');
    }
  }


}


